/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  LinkHighlight,
  LinkStandalone,
  MessageCallout,
  MessageVariety,
  toast,
} from '@sonarsource/echoes-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SharedDocLink, useSharedDocUrl } from '~adapters/helpers/docs';
import { useDeleteJiraBindingMutation, usePatchJiraBindingMutation } from '../../queries';
import type { JiraOrganizationBinding } from '../../types';
import { OrganizationJiraAppConfiguration } from './OrganizationJiraAppConfiguration';
import OrganizationJiraGrantPermissionsSwitch from './OrganizationJiraGrantPermissionsSwitch';
import { OrganizationJiraInstanceDetails } from './OrganizationJiraInstanceDetails';
import { OrganizationJiraIntro } from './OrganizationJiraIntro';

interface OrganizationJiraConnectedProps {
  binding: JiraOrganizationBinding;
  isCalloutShown: boolean;
  isCustomJiraApp: boolean;
  isPending: boolean;
  onReauthorize: () => void;
  organizationKey: string;
  setIsCalloutShown?: (value: boolean) => void;
  sonarOrganizationName?: string;
  withProjectPermissionsSwitch?: boolean;
}

export function OrganizationJiraConnected({
  binding,
  isCalloutShown,
  isCustomJiraApp,
  organizationKey,
  isPending,
  onReauthorize,
  withProjectPermissionsSwitch,
  setIsCalloutShown,
  sonarOrganizationName,
}: Readonly<OrganizationJiraConnectedProps>) {
  const { formatMessage } = useIntl();
  const docUrl = useSharedDocUrl();

  const { isPending: isPatchingBinding } = usePatchJiraBindingMutation();
  const { mutate: disconnectBinding, isPending: isDeletingBinding } =
    useDeleteJiraBindingMutation();

  const onDisconnect = () => {
    disconnectBinding(binding.sonarOrganizationUuid, {
      onSuccess: () => {
        toast.success({
          description: <FormattedMessage id="organization.jira.binding.unbind_success" />,
          duration: 'short',
          isDismissable: true,
        });
      },
    });
  };

  const CalloutMessage = isCalloutShown && (
    <MessageCallout
      action={
        !withProjectPermissionsSwitch && (
          <div className="sw-mt-4">
            <LinkStandalone
              className="sw-ml-3"
              enableOpenInNewTab
              highlight={LinkHighlight.Default}
              to={docUrl(SharedDocLink.JiraIntegration)}
            >
              <FormattedMessage id="organization.jira.binding.connected.callout.documentation_link_text" />
            </LinkStandalone>
          </div>
        )
      }
      onDismiss={() => {
        setIsCalloutShown?.(false);
      }}
      title={formatMessage({ id: 'organization.jira.binding.connected.callout.title' })}
      variety={MessageVariety.Info}
    >
      <FormattedMessage id="organization.jira.binding.connected.callout.text" />
    </MessageCallout>
  );

  return (
    <div className="sw-flex sw-flex-col sw-gap-6">
      {!withProjectPermissionsSwitch && CalloutMessage}

      <OrganizationJiraIntro isConnected sonarOrganizationName={sonarOrganizationName} />

      {withProjectPermissionsSwitch && (
        <>
          {CalloutMessage}
          <OrganizationJiraGrantPermissionsSwitch
            binding={binding}
            organizationKey={organizationKey}
          />
        </>
      )}

      <OrganizationJiraInstanceDetails
        binding={binding}
        isDeletingBinding={isDeletingBinding}
        isPatchingBinding={isPatchingBinding}
        onDisconnect={onDisconnect}
        onReauthorize={onReauthorize}
      />

      {isCustomJiraApp && (
        <OrganizationJiraAppConfiguration
          isPendingUpdate={isPending}
          onUpdatedAppConfiguration={onReauthorize}
        />
      )}
    </div>
  );
}
